import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Mail, Lock, User, Loader2, Check, X } from "lucide-react";
import { Button } from "@/components/flowly/infra/Button/Button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { useAuth } from "@/contexts/AuthContext";
import { useAuthModal } from "@/contexts/AuthModalContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { toast } from "@/lib/toast";
import { cn } from "@/lib/utils";
import { fetchClientIp } from "@/lib/fetchClientIp";
import { AuthModalGoogleButton } from "./AuthModalGoogleButton";

const SYMBOL_REGEX = /[!@#$%^&*()_+\-=[\]{};':"\\|<>?,./`~]/;

function usePasswordRequirements(password: string) {
  return useMemo(() => ({
    length: password.length >= 8,
    uppercase: /[A-Z]/.test(password),
    lowercase: /[a-z]/.test(password),
    digit: /[0-9]/.test(password),
    symbol: SYMBOL_REGEX.test(password),
  }), [password]);
}

export function AuthModalSignupForm() {
  const [displayName, setDisplayName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const { signUp, signInWithGoogle } = useAuth();
  const { closeAuthModal, switchAuthMode, openVerifyEmailModal, redirectAfterSuccess } = useAuthModal();
  const { t } = useLanguage();
  const navigate = useNavigate();

  const requirements = usePasswordRequirements(password);
  const allRequirementsMet = Object.values(requirements).every(Boolean);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!allRequirementsMet) {
      toast.error(t.auth.passwordMinLength);
      return;
    }

    setIsLoading(true);

    // Only fetch the client IP when the user actually opted in — no point
    // hitting ipify (or storing an IP) for users who declined marketing.
    const consentIp = marketingConsent ? await fetchClientIp() : null;
    const { error, session } = await signUp(
      email,
      password,
      displayName,
      marketingConsent,
      consentIp,
    );

    if (error) {
      toast.error(t.auth.signupError, {
        description: error.message,
      });
    } else if (!session) {
      closeAuthModal();
      openVerifyEmailModal(email, true);
    } else {
      toast.success(t.auth.signupSuccess, {
        description: t.auth.welcomeToFlowly,
      });
      const redirect = redirectAfterSuccess;
      closeAuthModal();
      if (redirect) navigate(redirect);
    }

    setIsLoading(false);
  };

  const handleGoogleSignUp = async () => {
    setIsGoogleLoading(true);
    const { error } = await signInWithGoogle();
    if (error) {
      toast.error(t.auth.googleSignupError, {
        description: error.message,
      });
      setIsGoogleLoading(false);
    }
    // Don't set loading to false on success - will redirect
  };

  return (
    <div>
      <div className="text-center mb-6">
        <h2 className="text-2xl font-bold text-foreground mb-1">{t.auth.createNewAccount}</h2>
        <p className="text-muted-foreground text-sm">{t.auth.startCreatingFree}</p>
      </div>

      <AuthModalGoogleButton
        onClick={handleGoogleSignUp}
        isLoading={isGoogleLoading}
        label={t.auth.continueWithGoogle}
      />

      <div className="relative my-6">
        <Separator />
        <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-card px-3 text-xs text-muted-foreground">
          {t.auth.or}
        </span>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="signup-displayName" className="text-start block">
            {t.profile.displayName}
          </Label>
          <div className="relative">
            <User className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <Input
              id="signup-displayName"
              type="text"
              placeholder={t.auth.yourName}
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              className="pr-10"
              required
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="signup-email" className="text-start block">
            {t.auth.email}
          </Label>
          <div className="relative">
            <Mail className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <Input
              id="signup-email"
              type="email"
              placeholder="your@email.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="pr-10 text-left"
              dir="ltr"
              required
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="signup-password" className="text-start block">
            {t.auth.password}
          </Label>
          <div className="relative">
            <Lock className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <Input
              id="signup-password"
              type="password"
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="pr-10 text-left"
              dir="ltr"
              required
              minLength={8}
            />
          </div>
          {password.length > 0 && (
            <ul className="grid grid-cols-2 gap-x-3 gap-y-0.5 text-xs mt-1.5">
              {([
                ["length", t.auth.passwordReqLength],
                ["uppercase", t.auth.passwordReqUppercase],
                ["lowercase", t.auth.passwordReqLowercase],
                ["digit", t.auth.passwordReqDigit],
                ["symbol", t.auth.passwordReqSymbol],
              ] as const).map(([key, label]) => (
                <li key={key} className={cn(
                  "flex items-center gap-1",
                  requirements[key] ? "text-green-500" : "text-muted-foreground"
                )}>
                  {requirements[key] ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
                  {label}
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="flex items-start gap-2 pt-1">
          <Checkbox
            id="signup-marketing-consent"
            checked={marketingConsent}
            onCheckedChange={(checked) => setMarketingConsent(checked === true)}
            className="mt-0.5"
          />
          <Label
            htmlFor="signup-marketing-consent"
            className="text-xs text-muted-foreground font-normal leading-snug cursor-pointer text-start"
          >
            {t.auth.marketingConsent}
          </Label>
        </div>

        <Button type="submit" variant="primary" className="w-full" disabled={isLoading}>
          {isLoading ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              {t.auth.creatingAccount}
            </>
          ) : (
            t.auth.signupTitle
          )}
        </Button>
      </form>

      <div className="mt-6 text-center">
        <p className="text-muted-foreground text-sm">
          {t.auth.hasAccountQuestion}{" "}
          <Button
            type="button"
            variant="ghost"
            size="xs"
            onClick={switchAuthMode}
            className="text-primary hover:underline font-medium !p-0"
          >
            {t.auth.loginTitle}
          </Button>
        </p>
      </div>
    </div>
  );
}
